import { Alert, Button, Slide, Snackbar, styled } from '@mui/material';
import { SlideProps } from '@mui/material/Slide';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { LocalDataTrack } from 'twilio-video';
import { useTranslation } from 'react-i18next';
import ControlPanel from '../components/ControlPanel/ControlPanel';
import ControlsPractitioner from '../components/ControlsPractitioner/ControlsPractitioner';
import LocalVideoPreview from '../components/LocalVideoPreview/LocalVideoPreview';
import PractitionerControlsProvider from '../components/PractionerControlsProvider';
import {
  PractitionerMediaPreviewHost,
  PractitionerMediaPreviewPortal,
} from '../components/PractitionerMediaPreviewPlayer/PractitionerMediaPreviewReversePortal';
import Room from '../components/Room/Room';
import VideoLoader from '../components/VideoLoader/VideoLoader';
import useAppContext from '../hooks/useAppContext/useAppContext';
import useAuthorizedPractitioner from '../hooks/useAuthorizedPractitioner/useAuthorizedPractitioner';
import useCurrentSessionState from '../hooks/useCurrentSessionState/useCurrentSessionState';
import useRoomState from '../hooks/useRoomState/useRoomState';
import useVideoContext from '../hooks/useVideoContext/useVideoContext';
import useVideoViewToggle from '../hooks/useVideoViewToggle/useVideoViewToggle';
import './PractitionerPage.scss';
import BackendService from '../services/BackendService';
import MediaProvider from '../components/MediaProvider';
import UploadProvider from '../components/MediaUploadProvider';
import SessionCheckDialog from '../components/SessionCheckDialog/SessionCheckDialog';
import PermissionReminder from '../helpers/PermissionReminder';
import permissionImage from '../assets/device-permission-reminder.png';
import waitingForClientIMG from '../assets/fail_to_start_call.png';
import { requestMedia } from '../utils/requestMedia';
import { metrics } from '../metrics/sentryMetrics';
import { getMediaPermissionStatus } from '../utils/mediaPermissions';
import { ensureLocalMediaTracks } from '../utils/ensureLocalMediaTracks';
import {
  ALERT_TRANSITION_MS,
  CALL_CONNECT_TIMEOUT_MS,
  CALL_START_TOKEN_TIMEOUT_MS,
  NETWORK_RECONNECT_BANNER_LINGER_MS,
  NETWORK_RECONNECT_TIMEOUT_MS,
} from '../utils/consts';

const Container = styled('div')({
  display: 'flex',
  gridTemplateRows: 'auto 1fr',
});

const Main = styled('main')({
  overflow: 'hidden',
  width: '75%',
  height: '100%',
  position: 'relative',

  '& .practitioner-preview .player-container': {
    '& .player-overlay': {
      paddingTop: '56.25%',
      bottom: 'unset',
      '& .react-player': {
        position: 'absolute',
        top: 0,
        left: 0,
        minHeight: 'unset',
      },
    },
    '& .player-overlay-image': {
      position: 'absolute',
      top: 0,
      left: 0,
      minHeight: 'unset',
    },
  },
});

const Videoframe = styled('div')({
  height: '100%',
  width: '100%',
});

interface IVideoViewProps {
  isEnabled: boolean;
}

function ToggleVideoView(props: IVideoViewProps) {
  return props.isEnabled ? <Video /> : null;
}

const Video = () => {
  const roomState = useRoomState();

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <div style={{ position: 'absolute', zIndex: 1, width: '250px', bottom: '80px' }}>
        <LocalVideoPreview />
      </div>
      <Videoframe>{roomState === 'disconnected' ? <LocalVideoPreview /> : <Room />}</Videoframe>
    </div>
  );
};

function setWindowSize() {
  const width = 100 + 'vw';
  const height = 100 + 'vh';
  return [width, height];
}

const AlertTransition = (props: SlideProps) => <Slide {...props} direction="left" />;

const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutMs);

    promise
      .then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });

export default function PractitionerPage(props: any) {
  useAuthorizedPractitioner();
  const { sessionId, treatmentId } = useAppContext();
  const roomState = useRoomState();
  const [practitionerIsConnected, setPractitionerIsConnected] = useState<boolean>(false);
  const [width, height] = setWindowSize();
  const { isEnabled, setVideoViewState } = useVideoViewToggle(false);
  const { room, connect, localTracks, getLocalAudioTrack, getLocalVideoTrack } = useVideoContext();
  const [collapsed, setCollapsed] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [clientIsConnected, setClientIsConnected] = useState(false);
  const [callAbortSignal, setCallAbortSignal] = useState(0);
  const [userStoppedCall, setUserStoppedCall] = useState(false);
  const [requiresManualRecall, setRequiresManualRecall] = useState(false);
  const [callStartFailed, setCallStartFailed] = useState(false);
  const [offlineNotificationOpen, setOfflineNotificationOpen] = useState(false);
  const [participantIsReconnecting, setParticipantIsReconnecting] = useState(false);
  const networkReconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const networkReconnectBannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const networkReconnectWasActiveRef = useRef(false);
  const [showNetworkReconnectingBanner, setShowNetworkReconnectingBanner] = useState(false);
  const { t } = useTranslation();
  const currentState = useCurrentSessionState() || {};
  const [waitingForClient, setWaitingForClient] = useState(false);
  const controlPanelStopCallHandlerRef = useRef<(() => void) | null>(null);
  const initialPermissionReminderCheckedRef = useRef(false);

  const mediaPreviewPortalNodeRef = useRef<HTMLDivElement | null>(null);

  if (!mediaPreviewPortalNodeRef.current && typeof document !== 'undefined') {
    mediaPreviewPortalNodeRef.current = document.createElement('div');
  }

  const mediaPreviewPortalNode = mediaPreviewPortalNodeRef.current;
  const mediaPreviewClassName = isEnabled ? 'client-preview' : 'practitioner-preview';

  const handleDisconnectUser = useCallback(() => {
    if (room && typeof room.disconnect === 'function') {
      room.disconnect();
      setPractitionerIsConnected(false);
      setVideoViewState(false);
    }
  }, [room, setVideoViewState]);

  const StopCall = useCallback(() => {
    if (room && typeof room.disconnect === 'function') {
      room.disconnect();
    }
    setVideoViewState(false);
  }, [room, setVideoViewState]);

  const [showPermissionReminder, setShowPermissionReminder] = useState(false);
  const [token, setToken] = useState<string>('');

  const ensureCallLocalMediaTracks = useCallback(
    () =>
      ensureLocalMediaTracks({
        localTracks,
        getLocalAudioTrack,
        getLocalVideoTrack,
      }),
    [getLocalAudioTrack, getLocalVideoTrack, localTracks]
  );

  useEffect(() => {
    const roomAny = room as any;
    const handleParticipantDisconnected = () => setParticipantIsReconnecting(false);
    const handleParticipantReconnecting = () => setParticipantIsReconnecting(true);
    const handleParticipantReconnected = () => setParticipantIsReconnecting(false);

    // Recommended cleanup on browser unload
    window.addEventListener('unload', handleDisconnectUser);

    // Only add listeners if room exists
    if (roomAny && typeof roomAny.on === 'function') {
      roomAny.on('participantDisconnected', handleParticipantDisconnected);
      roomAny.on('participantReconnecting', handleParticipantReconnecting);
      roomAny.on('participantReconnected', handleParticipantReconnected);
    }

    return () => {
      window.removeEventListener('unload', handleDisconnectUser);

      if (roomAny && typeof roomAny.off === 'function') {
        roomAny.off('participantDisconnected', handleParticipantDisconnected);
        roomAny.off('participantReconnecting', handleParticipantReconnecting);
        roomAny.off('participantReconnected', handleParticipantReconnected);
      }
    };
  }, [room, handleDisconnectUser]);

  useEffect(() => {
    if (initialPermissionReminderCheckedRef.current) {
      return;
    }

    initialPermissionReminderCheckedRef.current = true;
    let isMounted = true;

    const checkInitialPermissions = async () => {
      const permissionStatus = await getMediaPermissionStatus();

      if (isMounted && permissionStatus.shouldShowInitialReminder) {
        setShowPermissionReminder(true);
      }
    };

    void checkInitialPermissions();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleOnline = () => {
      setOfflineNotificationOpen(false);
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    const resetPLayerState = async () => {
      await BackendService.sendMediaVisibility(sessionId, treatmentId, '', false);
      await BackendService.sendMediaCommand(sessionId, treatmentId, '', 1, 0);
    };
    resetPLayerState();
  }, [sessionId, treatmentId]);

  const isNetworkReconnecting =
    (roomState === 'reconnecting' || participantIsReconnecting) &&
    practitionerIsConnected &&
    !waitingForClient &&
    !requiresManualRecall &&
    !callStartFailed &&
    !userStoppedCall;

  useEffect(() => {
    if (networkReconnectWasActiveRef.current !== isNetworkReconnecting) {
      networkReconnectWasActiveRef.current = isNetworkReconnecting;
      metrics.count('call.state.transition', 1, {
        state: isNetworkReconnecting ? 'network_reconnecting' : 'network_reconnected',
      });
    }

    if (networkReconnectTimeoutRef.current !== null) {
      clearTimeout(networkReconnectTimeoutRef.current);
      networkReconnectTimeoutRef.current = null;
    }

    if (!isNetworkReconnecting) {
      return;
    }

    networkReconnectTimeoutRef.current = setTimeout(() => {
      const stillReconnecting =
        (roomState === 'reconnecting' || participantIsReconnecting) &&
        practitionerIsConnected &&
        !waitingForClient &&
        !requiresManualRecall &&
        !callStartFailed &&
        !userStoppedCall;

      if (!stillReconnecting) {
        return;
      }

      setWaitingForClient(false);
      setRequiresManualRecall(true);
      setIsConnecting(false);
      setPractitionerIsConnected(false);
      setClientIsConnected(false);
      StopCall();
      metrics.count('call.state.transition', 1, {
        state: 'recall_required',
        reason: 'network_reconnect_timeout',
      });
    }, NETWORK_RECONNECT_TIMEOUT_MS);

    return () => {
      if (networkReconnectTimeoutRef.current !== null) {
        clearTimeout(networkReconnectTimeoutRef.current);
        networkReconnectTimeoutRef.current = null;
      }
    };
  }, [
    isNetworkReconnecting,
    roomState,
    participantIsReconnecting,
    practitionerIsConnected,
    waitingForClient,
    requiresManualRecall,
    callStartFailed,
    userStoppedCall,
    StopCall,
  ]);

  useEffect(() => {
    if (networkReconnectBannerTimeoutRef.current !== null) {
      clearTimeout(networkReconnectBannerTimeoutRef.current);
      networkReconnectBannerTimeoutRef.current = null;
    }

    if (isNetworkReconnecting) {
      setShowNetworkReconnectingBanner(true);
      return;
    }

    if (!showNetworkReconnectingBanner) {
      return;
    }

    networkReconnectBannerTimeoutRef.current = setTimeout(() => {
      setShowNetworkReconnectingBanner(false);
      networkReconnectBannerTimeoutRef.current = null;
    }, NETWORK_RECONNECT_BANNER_LINGER_MS);

    return () => {
      if (networkReconnectBannerTimeoutRef.current !== null) {
        clearTimeout(networkReconnectBannerTimeoutRef.current);
        networkReconnectBannerTimeoutRef.current = null;
      }
    };
  }, [isNetworkReconnecting, showNetworkReconnectingBanner]);

  useEffect(() => {
    if (requiresManualRecall || callStartFailed || userStoppedCall || !practitionerIsConnected) {
      if (networkReconnectBannerTimeoutRef.current !== null) {
        clearTimeout(networkReconnectBannerTimeoutRef.current);
        networkReconnectBannerTimeoutRef.current = null;
      }
      setShowNetworkReconnectingBanner(false);
    }
  }, [requiresManualRecall, callStartFailed, userStoppedCall, practitionerIsConnected]);

  const ConnectToRoom = async (token: string, dataTrack: LocalDataTrack) => {
    // check if user provided access to the video and microphone devices
    const success = await requestMedia({ setPermission: setShowPermissionReminder });

    if (!success) {
      setPractitionerIsConnected(false);
      setClientIsConnected(false);
      setIsConnecting(false);
      StopCall();
      return;
    }

    setShowPermissionReminder(false);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setIsConnecting(false);
      setPractitionerIsConnected(false);
      setClientIsConnected(false);
      setVideoViewState(false);
      setWaitingForClient(false);
      setCallStartFailed(false);
      setRequiresManualRecall(false);
      setOfflineNotificationOpen(true);
      metrics.count('call.state.transition', 1, {
        state: 'call_start_blocked',
        stage: 'offline_connect_precheck',
      });
      return;
    }

    try {
      await ensureCallLocalMediaTracks();
      await withTimeout(connect(token, dataTrack), CALL_CONNECT_TIMEOUT_MS, 'Twilio connect timed out');
      setIsConnecting(false);
      setPractitionerIsConnected(true);
      setVideoViewState(true);
      setWaitingForClient(false);
      setRequiresManualRecall(false);
      setCallStartFailed(false);
      setUserStoppedCall(false);
      metrics.count('call.state.transition', 1, { state: 'connected' });
    } catch (err) {
      console.error('[Twilio Connect Error]', err);
      setIsConnecting(false);
      setPractitionerIsConnected(false);
      setVideoViewState(false);
      setWaitingForClient(false);
      setCallStartFailed(true);
      setRequiresManualRecall(false);
      metrics.count('call.state.transition', 1, {
        state: 'call_start_failed',
        stage: err?.message === 'Twilio connect timed out' ? 'connect_timeout' : 'connect_to_room',
      });
    }
  };

  const handleUserStoppedCall = useCallback(() => {
    setUserStoppedCall(true);
    setCallAbortSignal((value) => value + 1);
    setWaitingForClient(false);
    setRequiresManualRecall(false);
    setCallStartFailed(false);
    setParticipantIsReconnecting(false);
    metrics.count('call.state.transition', 1, { state: 'manual_stop' });
  }, []);

  const handleRecallRequired = useCallback(() => {
    setWaitingForClient(false);
    setRequiresManualRecall(true);
    setCallStartFailed(false);
    metrics.count('call.state.transition', 1, {
      state: 'recall_required',
      reason: 'client_wait_timeout',
    });
  }, []);

  const abortCallAndRetries = useCallback(() => {
    handleUserStoppedCall();
    setPractitionerIsConnected(false);
    setClientIsConnected(false);
    setIsConnecting(false);
    StopCall();
  }, [handleUserStoppedCall, StopCall]);

  const stopCallFromBanner = useCallback(() => {
    const stopHandler = controlPanelStopCallHandlerRef.current;
    if (typeof stopHandler === 'function') {
      stopHandler();
      return;
    }

    abortCallAndRetries();
  }, [abortCallAndRetries]);

  const registerControlPanelStopCallHandler = useCallback((handler: () => void) => {
    controlPanelStopCallHandlerRef.current = handler;
  }, []);

  const toggleCallHasStarted = async () => {
    setUserStoppedCall(false);
    setRequiresManualRecall(false);
    setCallStartFailed(false);
    setOfflineNotificationOpen(false);

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setIsConnecting(false);
      setCallStartFailed(false);
      setOfflineNotificationOpen(true);
      metrics.count('call.state.transition', 1, {
        state: 'call_start_blocked',
        stage: 'offline_start_precheck',
      });
      return;
    }

    setIsConnecting(true);
    metrics.count('call.action.start_attempt', 1);
    const mediaAccessGranted = await requestMedia({ setPermission: setShowPermissionReminder });

    if (!mediaAccessGranted) {
      setPractitionerIsConnected(false);
      setClientIsConnected(false);
      setVideoViewState(false);
      setIsConnecting(false);
      return;
    }

    setShowPermissionReminder(false);

    try {
      await ensureCallLocalMediaTracks();
      const token = await withTimeout(
        BackendService.getToken(sessionId, treatmentId),
        CALL_START_TOKEN_TIMEOUT_MS,
        'Token request timed out'
      );
      setToken(token);
    } catch (error) {
      console.error('[Call Start Error]', error);
      setPractitionerIsConnected(false);
      setClientIsConnected(false);
      setVideoViewState(false);
      setCallStartFailed(true);
      setToken('');
      metrics.count('call.state.transition', 1, {
        state: 'call_start_failed',
        stage: error instanceof Error && error.message === 'Token request timed out' ? 'token_timeout' : 'token_fetch',
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleRetryCall = useCallback(() => {
    setUserStoppedCall(false);
    setRequiresManualRecall(false);
    setCallStartFailed(false);
    metrics.count('call.action.retry_manual', 1);
    void toggleCallHasStarted();
  }, [toggleCallHasStarted]);

  const showNetworkReconnectingAlert =
    showNetworkReconnectingBanner &&
    practitionerIsConnected &&
    !requiresManualRecall &&
    !callStartFailed &&
    !userStoppedCall;
  const showWaitingForClientAlert =
    waitingForClient && practitionerIsConnected && !clientIsConnected && !requiresManualRecall && !callStartFailed;

  const stopCallBannerAction = (
    <Button color="inherit" size="small" onClick={stopCallFromBanner}>
      {t('stop-call') as string}
    </Button>
  );

  const activeBanner = showNetworkReconnectingAlert
    ? {
        severity: 'info' as const,
        message: 'connection-interrupted',
        testId: 'pwid-practitioner-network-reconnecting',
        action: stopCallBannerAction,
        zIndex: 1301,
      }
    : showWaitingForClientAlert
      ? {
          severity: 'warning' as const,
          message: 'reconnection-waiting-client',
          testId: 'pwid-practitioner-waiting',
          action: stopCallBannerAction,
          zIndex: 1300,
        }
      : offlineNotificationOpen
        ? {
            severity: 'error' as const,
            message: 'connection-call-failed',
            testId: 'pwid-practitioner-offline-notification',
            action: undefined,
            zIndex: 1300,
          }
        : null;
  const activePermissionReminder = showPermissionReminder
    ? {
        imageSrc: permissionImage,
        buttonLabel: 'okay-internet-report',
        message: 'permission-allow-camera-mic',
        testId: 'pwid-practitioner-permission',
        onClose: () => setShowPermissionReminder(false),
      }
    : requiresManualRecall
      ? {
          imageSrc: waitingForClientIMG,
          buttonLabel: 'retry',
          closeLabel: 'stop-call',
          message: 'reconnection-call-again',
          testId: 'pwid-practitioner-recall-required',
          onClose: abortCallAndRetries,
          onRetry: handleRetryCall,
        }
      : callStartFailed
        ? {
            imageSrc: waitingForClientIMG,
            buttonLabel: 'retry',
            closeLabel: 'stop-call',
            message: 'reconnection-failed-call-again',
            testId: 'pwid-practitioner-call-start-failed',
            onClose: abortCallAndRetries,
            onRetry: handleRetryCall,
          }
        : null;

  return (
    <Container style={{ height, width }}>
      <PractitionerControlsProvider>
        <MediaProvider>
          <UploadProvider>
            <PractitionerMediaPreviewPortal portalNode={mediaPreviewPortalNode} className={mediaPreviewClassName} />
            <VideoLoader isLoading={isConnecting} />
            {collapsed ? null : (
              <Main className={'main'}>
                <ToggleVideoView isEnabled={isEnabled} />
                <PractitionerMediaPreviewHost portalNode={mediaPreviewPortalNode} isActive={!isEnabled} />
              </Main>
            )}
            <ControlsPractitioner currentState={currentState} />
            <div className={`${collapsed ? 'right-full-width' : 'right'}`}>
              {/*<CollapseToggle isCollapsed={collapsed} handleCollapse={() => setCollapsed(!collapsed)} />*/}
              <ControlPanel
                ConnectToRoom={ConnectToRoom}
                StopCall={StopCall}
                practitionerIsConnected={practitionerIsConnected}
                isNetworkReconnecting={isNetworkReconnecting}
                setPractitionerIsConnected={setPractitionerIsConnected}
                currentState={currentState}
                isCallActive={isEnabled}
                clientIsConnected={clientIsConnected}
                setClientIsConnected={setClientIsConnected}
                setWaitingForClient={setWaitingForClient}
                toggleCallHasStarted={toggleCallHasStarted}
                token={token}
                setToken={setToken}
                callAbortSignal={callAbortSignal}
                userStoppedCall={userStoppedCall}
                onRecallRequired={handleRecallRequired}
                onUserStoppedCall={handleUserStoppedCall}
                registerStopCallHandler={registerControlPanelStopCallHandler}
                mediaPreviewPortalNode={mediaPreviewPortalNode}
              />
            </div>
            <SessionCheckDialog sessionId={sessionId} treatmentId={treatmentId} />
            {activeBanner ? (
              <Snackbar
                open
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{ zIndex: activeBanner.zIndex }}
                onClose={
                  activeBanner.testId === 'pwid-practitioner-offline-notification'
                    ? () => setOfflineNotificationOpen(false)
                    : undefined
                }
                TransitionComponent={AlertTransition}
                transitionDuration={ALERT_TRANSITION_MS}
              >
                <Alert
                  severity={activeBanner.severity}
                  variant="filled"
                  data-testid={activeBanner.testId}
                  action={activeBanner.action}
                >
                  {t(activeBanner.message)}
                </Alert>
              </Snackbar>
            ) : null}
            <PermissionReminder
              open={Boolean(activePermissionReminder)}
              onClose={activePermissionReminder?.onClose ?? (() => undefined)}
              onRetry={activePermissionReminder?.onRetry}
              imageSrc={activePermissionReminder?.imageSrc}
              buttonLabel={activePermissionReminder?.buttonLabel}
              closeLabel={activePermissionReminder?.closeLabel}
              message={activePermissionReminder?.message}
              pwid={activePermissionReminder?.testId}
            />
          </UploadProvider>
        </MediaProvider>
      </PractitionerControlsProvider>
    </Container>
  );
}
